import React, { useEffect, useMemo, useRef, useState } from 'react';
import PlannedSchedule from './PlannedSchedule';
import { useAuth } from '../../context/AuthContext';
import { canWrite } from '../../utils/roles';
import RRHHNativeShell from './RRHHNativeShell';
import JournalMarksEditor from './JournalMarksEditor';
import { rrhhCoreGet, rrhhCorePatch, rrhhCorePost } from './rrhhCoreApi';
import { currentIsoDate, formatDisplayDate, normalizeStatus, operationBandsForTariff, operationTariffLabel } from './rrhhUtils';
import './RRHHNative.css';

const PAGE_SIZE = 50;

function addDays(dateValue, amount) {
  const date = new Date(`${dateValue}T12:00:00`);
  date.setDate(date.getDate() + amount);
  return date.toISOString().slice(0, 10);
}

function personLabel(row) {
  return row.persona || row.persona_nombre || `Persona ${row.persona_id}`;
}

function journalState(row) {
  return normalizeStatus(row.estado_aprobacion || row.estado_validacion || row.estado || 'PENDIENTE');
}

function operationState(row) {
  return normalizeStatus(row.estado || 'PENDIENTE');
}

function phaseLabel(value) {
  const normalized = String(value || '').toLocaleLowerCase('es');
  if (normalized === 'evento') return 'Operación';
  if (normalized === 'armado') return 'Armado';
  if (normalized === 'desarmado') return 'Desarmado';
  if (normalized === 'task' || normalized === 'tarea') return 'Tarea';
  return value || 'Agenda';
}

function timeRange(context) {
  const start = String(context?.start_time || '').trim();
  const end = String(context?.end_time || '').trim();
  if (start && end) return `${start} - ${end}`;
  return start || end || '';
}

function OperationAgendaContext({ contexts }) {
  const items = Array.isArray(contexts) ? contexts : [];
  return (
    <div className="rrhh-operation-agenda-context">
      <button className="rrhh-operation-reference-trigger" type="button">
        Referencia{items.length ? <span>{items.length}</span> : null}
      </button>
      <div className="rrhh-operation-agenda-popover" role="tooltip">
        {items.length ? items.map((context, index) => {
          const title = context.event_name || context.entity_name || 'Agenda';
          const section = context.section && context.section !== 'General' ? context.section : '';
          const details = [context.place, section, context.role, timeRange(context)].filter(Boolean).join(' · ');
          return (
            <div key={`${context.assignment_id || context.entity_id || index}-${index}`}>
              <strong>{phaseLabel(context.phase || context.entity_type)} · {title}</strong>
              {details && <span>{details}</span>}
            </div>
          );
        }) : <div className="empty">Sin asignación de agenda para esta persona en ese día.</div>}
      </div>
    </div>
  );
}

function rowKey(row, isOperations) {
  return isOperations ? String(row.id) : `${row.turno_id}|${row.persona_id}|${row.fecha}`;
}

function StateBadge({ state }) {
  const normalized = normalizeStatus(state);
  const className = normalized === 'APROBADA' ? 'badge badge-green'
    : ['NO_APROBADA', 'RECHAZADA'].includes(normalized) ? 'badge badge-red'
      : 'badge badge-yellow';
  return <span className={className}>{normalized.replace('_', ' ')}</span>;
}

export default function ValidationPage({ kind }) {
  const { user } = useAuth();
  const isOperations = kind === 'operations';
  const canEdit = canWrite(user, isOperations ? 'rrhh.operaciones' : 'rrhh.jornales');
  const today = currentIsoDate();
  const sourceParams = new URLSearchParams(window.location.search);
  const [from, setFrom] = useState(sourceParams.get('desde') || addDays(today, -7));
  const [to, setTo] = useState(sourceParams.get('hasta') || today);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('todos');
  const [selected, setSelected] = useState(new Set());
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [operationTariffs, setOperationTariffs] = useState([]);
  const [page, setPage] = useState(1);
  const requestSequence = useRef(0);

  const endpoint = isOperations ? '/validacion/operaciones' : '/validacion/jornales';

  async function refresh() {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ desde: from, hasta: to });
      if (sourceParams.get('persona_id')) params.set('persona_id', sourceParams.get('persona_id'));
      const data = await rrhhCoreGet(`${endpoint}?${params}`);
      if (sequence !== requestSequence.current) return;
      setRows(Array.isArray(data) ? data.filter(row=>!isOperations||!sourceParams.get('operacion_id')||String(row.id)===sourceParams.get('operacion_id')) : []);
      setSelected(new Set());
    } catch (err) {
      if (sequence !== requestSequence.current) return;
      setError(err.message || 'No se pudieron cargar los registros');
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, [from, to]);

  useEffect(() => {
    if (!isOperations) return;
    let cancelled = false;
    rrhhCoreGet('/operacion-tarifas/validacion')
      .then((data) => {
        if (!cancelled) setOperationTariffs(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'No se pudieron cargar los tipos de operación');
      });
    return () => { cancelled = true; };
  }, [isOperations]);

  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [from, to, query, status, isOperations]);

  const visibleRows = useMemo(() => rows.filter((row) => {
    const normalizedQuery = query.toLocaleLowerCase('es');
    const agendaContextText = (Array.isArray(row.agenda_contextos) ? row.agenda_contextos : [])
      .map((context) => [
        context.event_code,
        context.event_name,
        context.entity_name,
        context.place,
        context.phase,
        context.section,
        context.role,
      ].filter(Boolean).join(' '))
      .join(' ');
    const matchesQuery = personLabel(row).toLocaleLowerCase('es').includes(normalizedQuery)
      || String(row.referencia || row.actividad_ubicacion || '').toLocaleLowerCase('es').includes(normalizedQuery)
      || agendaContextText.toLocaleLowerCase('es').includes(normalizedQuery);
    const state = isOperations ? operationState(row) : journalState(row);
    return matchesQuery && (status === 'todos' || state === status);
  }), [rows, query, status, isOperations]);

  const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return visibleRows.slice(start, start + PAGE_SIZE);
  }, [visibleRows, page]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  function toggle(id) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function applyState(nextState) {
    const ids = [...selected];
    if (!ids.length) return setError('Seleccioná al menos un registro');
    if (nextState === 'NO_APROBADA' && !comment.trim()) return setError('Agregá un comentario para no aprobar');
    setSaving(true);
    setError('');
    try {
      if (isOperations) {
        await Promise.all(ids.map((id) => rrhhCorePost(`/validacion/operaciones/${id}`, { estado: nextState, comentario: comment }))); 
      } else {
        const markIds = rows.filter((row) => selected.has(rowKey(row, false))).flatMap((row) => row.marcas_ids || [row.entrada_id, row.salida_id]).filter(Boolean);
        if (!markIds.length) throw new Error('Los jornales seleccionados no tienen marcas para validar');
        await rrhhCorePost('/validacion/aprobaciones', { marca_ids: markIds, estado: nextState, observacion: comment });
      }
      setComment('');
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo guardar la validación');
    } finally {
      setSaving(false);
    }
  }

  function tariffsForPerson(row) {
    const personnelId = Number(row.persona_id);
    return operationTariffs.filter((tariff) => Array.isArray(tariff.personnel_ids)
      && tariff.personnel_ids.some((id) => Number(id) === personnelId));
  }

  async function changeOperationTariff(row, tariffId, band) {
    const id = Number(tariffId);
    const nextBand = String(band || '').trim();
    if (!Number.isInteger(id) || id <= 0 || !nextBand) return;
    if (id === Number(row.operacion_tarifa_id) && nextBand === String(row.franja || '').trim()) return;
    setSaving(true);
    setError('');
    try {
      const updated = await rrhhCorePatch(`/validacion/operaciones/${row.id}/tarifa`, {
        operacion_tarifa_id: id,
        franja: nextBand,
      });
      setRows((current) => current.map((item) => (item.id === row.id ? { ...item, ...updated } : item)));
    } catch (err) {
      setError(err.message || 'No se pudo actualizar el tipo o la franja de operación');
    } finally {
      setSaving(false);
    }
  }

  async function applyJournalAction(row, action) {
    const confirmations = {
      AUSENTE: `¿Marcar ausente a ${personLabel(row)}? Las marcas existentes quedarán anuladas.`,
      APROBAR_Y_PLAN: `¿Reemplazar el horario previsto por las marcas reales de ${personLabel(row)} y aprobar la jornada?`,
    };
    if (confirmations[action] && !window.confirm(confirmations[action])) return;
    setSaving(true);
    setError('');
    try {
      await rrhhCorePost('/validacion/jornales/accion', {
        accion: action,
        turno_id: row.turno_id,
        persona_id: row.persona_id,
        fecha: row.fecha,
        marca_ids: row.marcas_ids || [],
        observacion: comment,
      });
      setComment('');
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo actualizar la jornada');
    } finally {
      setSaving(false);
    }
  }

  const title = isOperations ? 'Validación de operaciones' : 'Validación de jornales';
  const subtitle = isOperations
    ? 'Operaciones enviadas desde el reloj. Esta pantalla sólo valida su estado.'
    : 'Jornadas calculadas desde turnos, marcas y tramos canónicos.';

  return (
    <RRHHNativeShell title={title} subtitle={subtitle}>
      {error && <div className="alert alert-error">{error}</div>}
      <section className="rrhh-native-card rrhh-validation-controls">
        <label>Desde<input className="form-input" type="date" value={from} onChange={(event) => setFrom(event.target.value)} /></label>
        <label>Hasta<input className="form-input" type="date" value={to} onChange={(event) => setTo(event.target.value)} /></label>
        <label>Buscar<input className="form-input" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Persona, proyecto o detalle" /></label>
        <label>Estado<select className="form-input" value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="todos">Todos</option><option value="PENDIENTE">Pendiente</option><option value="APROBADA">Aprobada</option><option value={isOperations ? 'NO_APROBADA' : 'RECHAZADA'}>{isOperations ? 'No aprobada' : 'Rechazada'}</option>
        </select></label>
      </section>

      <section className="rrhh-native-card rrhh-validation-actions">
        <span>{selected.size} seleccionados</span>
        <input className="form-input" value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Comentario de validación" />
        <button className="btn btn-primary" type="button" disabled={saving || !canEdit} onClick={() => applyState('APROBADA')}>Aprobar selección</button>
        <button className="btn btn-secondary" type="button" disabled={saving || !canEdit} onClick={() => applyState('PENDIENTE')}>Pendiente</button>
        <button className="btn btn-danger" type="button" disabled={saving || !canEdit} onClick={() => applyState(isOperations ? 'NO_APROBADA' : 'RECHAZADA')}>{isOperations ? 'No aprobar' : 'Rechazar'}</button>
      </section>

      <section className={`rrhh-native-card rrhh-validation-table-wrap ${isOperations ? 'rrhh-operation-validation-table-wrap' : ''}`}>
        <table className={`rrhh-native-table ${!isOperations ? 'rrhh-journal-validation-table' : ''}`}>
          <thead><tr><th><input type="checkbox" checked={paginatedRows.length > 0 && paginatedRows.every((row) => selected.has(rowKey(row, isOperations)))} onChange={(event) => setSelected(event.target.checked ? new Set(paginatedRows.map((row) => rowKey(row, isOperations))) : new Set())} /></th><th>Fecha / persona</th><th>{isOperations ? 'Operación' : 'Plan semanal'}</th><th>{isOperations ? 'Referencia / Agenda' : 'Tramos registrados'}</th><th>Estado</th><th>Comentario</th>{!isOperations && <th>Acciones</th>}</tr></thead>
          <tbody>
            {loading && <tr><td colSpan="7">Cargando...</td></tr>}
            {!loading && !visibleRows.length && <tr><td colSpan="7">No hay registros para los filtros seleccionados.</td></tr>}
            {!loading && paginatedRows.map((row) => {
              const rowId = rowKey(row, isOperations);
              const state = isOperations ? operationState(row) : journalState(row);
              const marks = [row.entrada_hora, row.salida_hora].filter(Boolean).join(' / ') || 'Sin marcas';
              const allowedTariffs = isOperations ? tariffsForPerson(row) : [];
              const activeTariff = isOperations
                ? allowedTariffs.find((tariff) => Number(tariff.id) === Number(row.operacion_tarifa_id))
                : null;
              const bands = activeTariff ? operationBandsForTariff(activeTariff) : [];
              return <tr key={rowId}>
                <td><input type="checkbox" checked={selected.has(rowId)} onChange={() => toggle(rowId)} /></td>
                <td><strong>{personLabel(row)}</strong><br /><span className="text-sm text-dim">{formatDisplayDate(isOperations ? row.fecha_hora : row.fecha, { weekday: 'short' })}</span></td>
                <td>{isOperations ? <div className="rrhh-operation-type-cell">
                  <select
                    className="form-input rrhh-operation-type-select"
                    value={activeTariff?.id || ''}
                    disabled={saving || !canEdit || !allowedTariffs.length}
                    aria-label={`Tipo de operación de ${personLabel(row)}`}
                    onChange={(event) => {
                      const nextTariff = allowedTariffs.find((tariff) => Number(tariff.id) === Number(event.target.value));
                      const nextBands = operationBandsForTariff(nextTariff);
                      const retainedBand = nextBands.find((item) => item.band === row.franja);
                      changeOperationTariff(row, event.target.value, retainedBand?.band || nextBands[0]?.band);
                    }}
                  >
                    {!activeTariff && <option value="">Sin tipo habilitado</option>}
                    {allowedTariffs.map((tariff) => (
                      <option key={tariff.id} value={tariff.id}>{operationTariffLabel(tariff)}</option>
                    ))}
                  </select>
                  <select
                    className="form-input rrhh-operation-type-select"
                    value={bands.some((item) => item.band === row.franja) ? row.franja : ''}
                    disabled={saving || !canEdit || !activeTariff || !bands.length}
                    aria-label={`Franja de operación de ${personLabel(row)}`}
                    onChange={(event) => changeOperationTariff(row, activeTariff.id, event.target.value)}
                  >
                    {!bands.length && <option value="">Sin franja</option>}
                    {bands.map((band) => <option key={band.band} value={band.band}>{band.band} · ${Number(band.value || 0).toLocaleString('es-UY')}</option>)}
                  </select>
                  <span className="rrhh-operation-tariff-value">${Number(row.valor || 0).toLocaleString('es-UY')}</span>
                </div> : <PlannedSchedule turn={row} showDetails fallback={row.estado_turno || 'Sin horario'} />}</td>
                <td>{isOperations ? <div className="rrhh-operation-reference-cell">
                  <div className="rrhh-operation-reference-copy">
                    <span>{row.referencia || 'Sin referencia'}</span>
                    {row.observacion && <span className="text-sm text-dim">{row.observacion}</span>}
                  </div>
                  <OperationAgendaContext contexts={row.agenda_contextos} />
                </div> : <div className="rrhh-journal-inline-segments">
                  {(row.tramos || []).length ? row.tramos.map((segment, index) => <span key={`${segment.entrada_id || 'x'}-${segment.salida_id || index}`}><strong>{String(index + 1).padStart(2, '0')}</strong>{segment.entrada_hora || '—'} → {segment.salida_hora || 'abierto'}</span>) : <span className="text-dim">{marks}</span>}
                </div>}</td>
                <td><StateBadge state={state} /></td>
                <td>{row.validation_comment || row.motivo_rechazo || row.approval_note || '—'}</td>
                {!isOperations && <td><div className="rrhh-journal-row-actions">
                  <button className="btn btn-primary btn-sm" type="button" disabled={saving || !canEdit || !(row.marcas_ids || []).length} onClick={() => applyJournalAction(row, 'APROBAR')}>Aprobar turno</button>
                  <button className="btn btn-secondary btn-sm" type="button" disabled={saving || !canEdit} onClick={() => setEditingRow(row)}>Modificar turno</button>
                  <button className="btn btn-secondary btn-sm" type="button" disabled={saving || !canEdit || !(row.marcas_ids || []).length} onClick={() => applyJournalAction(row, 'APROBAR_Y_PLAN')}>Aprobar y pasar al plan</button>
                  <button className="btn btn-danger btn-sm" type="button" disabled={saving || !canEdit} onClick={() => applyJournalAction(row, 'AUSENTE')}>Marcar ausente</button>
                </div></td>}
              </tr>;
            })}
          </tbody>
        </table>
      </section>
      {!loading && visibleRows.length > 0 && <nav className="rrhh-validation-pagination" aria-label="Paginación de resultados">
        <span>
          {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, visibleRows.length)} de {visibleRows.length}
        </span>
        <div>
          <button className="btn btn-secondary btn-sm" type="button" disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>Anterior</button>
          <strong>Página {page} de {pageCount}</strong>
          <button className="btn btn-secondary btn-sm" type="button" disabled={page === pageCount} onClick={() => setPage((current) => Math.min(pageCount, current + 1))}>Siguiente</button>
        </div>
      </nav>}
      {editingRow && <JournalMarksEditor row={editingRow} onClose={() => setEditingRow(null)} onSaved={refresh} />}
    </RRHHNativeShell>
  );
}
